import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import Leads from "./pages/Leads";
import Proposals from "./pages/Proposals";
import EditProposal from "./pages/Proposals/components/EditProposal";
import DetailProposal from "./pages/Proposals/components/DetailProposal";
import ProposalPreview from "./pages/Proposals/components/ProposalPreview";
import Company from "./pages/Company";
import Clients from "./pages/Company/components/Clients";
import ClientsList from "./pages/Clients";
import AuditClientDetail from "./pages/AuditClientDetail";
import ClientProjectMatrix from "./pages/ClientProjectMatrix";
import SalesClientDashboard from "./pages/SalesClientDashboard";
import Login from "./components/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Loading from "./components/Loading";
import ProtectedRoute from "./components/ProtectedRoute";
import { useAuth } from "./contexts/AuthProvider";
import api from "./api";
import { usePmProjectMembership } from "./hooks/usePmProjectMembership";
import { resolveDashboardTier } from "./pages/AuditDashboard/dashboardRoleTiers";
import Invoice from "./pages/Invoice";
import InvoiceView from "./pages/Invoice/components/InvoiceView";
import "./styles/layout.css";
import "./components/common/AdvancedFilter/FilterAwarenessBar.css";
import VersionUpdateNotifier from "./components/common/VersionUpdateNotifier";
import Payment from "./pages/Payment";
import CreateTemplate from "./pages/CreateTemplate";
import ApprovalWorkflow from "./pages/ApprovalWorkflow";
import RateManagement from "./pages/RateManagement";
import CreateTypeUnit from "./pages/CreateTypeUnit";
import ProposalStageManagement from "./pages/ProposalStage";
import MasterCurrency from "./pages/MasterCurrency";
import CurrencyDetail from "./pages/MasterCurrency/CurrencyDetail";
import CreateRole from "./pages/CreateRole";
import FormatNumber from "./pages/FormatNumber";
import ExpenseCategory from "./pages/ExpenseCategory";
import ChartOfAccount from "./pages/ChartOfAccount";
import Expense from "./pages/Expense";
import ExpenseDetail from "./pages/Expense/ExpenseDetail";
import EmailTemplate from "./pages/EmailTemplate";
import EmailConfiguration from "./pages/EmailConfiguration";
import SetPermission from "./pages/SetPermission";
import Project from "./pages/Project";
import DetailProject from "./pages/Project/components/DetailProject";
import EditProject from "./pages/Project/components/EditProject";
import SalesProjectMonitoring from "./pages/SalesProjectMonitoring";
import Profile from "./pages/Profile";
import DevSelects from "./pages/DevSelects";
import Task from "./pages/Task";
import LeadDetail from "./pages/Leads/components/LeadDetail";
import ProspectsReports from "./pages/Reports/ProspectsReports";
import LeadsReports from "./pages/Reports/LeadsReports";
import ProposalsReports from "./pages/Reports/ProposalsReports";
import ProjectsReports from "./pages/Reports/ProjectsReports";
import SalesReport from "./pages/SalesReport";
import SalesTarget from "./pages/SalesTarget";
import Calendar from "./pages/Calendar";
import AuditSchedule from "./pages/AuditSchedule";
import AuditProcess from "./pages/AuditProcess";
import AuditOutputTemplate from "./pages/AuditOutputTemplate";
import AuditFindings from "./pages/AuditFindings";
import Analysis from "./pages/Analysis";
import CRMDashboard from "./pages/CRMDashboard";
import AuditDashboard from "./pages/AuditDashboard";
import AuditReconciliation from "./pages/AuditReconciliation";
import AuditQTransactionImportReview from "./pages/AuditQTransactionImportReview";
import AuditLibraries from "./pages/AuditLibraries";
import AuditFormCatalog from "./pages/AuditFormCatalog";
import AuditFormEntry from "./pages/AuditFormEntry";
import CertificateMonitoring from "./pages/CertificateMonitoring";
import CompetencyManagement from "./pages/CompetencyManagement";
import FinanceConfirmation from "./pages/FinanceConfirmation";
import LoadBalancer from "./pages/LoadBalancer";
import AuditorUtilization from "./pages/AuditorUtilization";
import CombinedDashboard from "./pages/CombinedDashboard";
import ProjectManagement from "./pages/ProjectManagement";
import PmDashboard from "./pages/PmDashboard";
import PmProjects from "./pages/PmProjects";
import PmProjectDetail from "./pages/PmProjectDetail";
import PmTimesheet from "./pages/PmTimesheet";
import AchievementForm from "./pages/SalesReport/AchievementForm";
import HRISEmployees from "./pages/HRIS/Employees";
import HRISEmployeeDetail from "./pages/HRIS/Employees/Detail";
import HRISExpenseClaim from "./pages/HRIS/Expenses/Claims/ExpenseClaim";
import Department from "./pages/HRIS/Department";
import JobTitle from "./pages/HRIS/JobTitle";
import Branch from "./pages/HRIS/Branch";
import DummyEmployeeManagementPage from "./pages/HRIS/EmployeeManagement/DummyEmployeeManagementPage";
import HRISLeaves from "./pages/HRIS/Leaves";
import HRISPayroll from "./pages/HRIS/Payroll";
import HRISPerformance from "./pages/HRIS/Performance";
import HRISRecruitment from "./pages/HRIS/Recruitment";
import DummyRecruitmentManagementPage from "./pages/HRIS/RecruitmentManagement/DummyRecruitmentManagementPage";
import HRISShiftAttendance from "./pages/HRIS/ShiftAttendance";
import HRISTaxBenefits from "./pages/HRIS/TaxBenefits";
import HRISTenure from "./pages/HRIS/Tenure";
import HRISExpenseClaimTypes from "./pages/HRIS/Expenses/Claims/ExpenseClaimTypes";
import HRISExpenseDashboard from "./pages/HRIS/Expenses/ExpenseDashboard";
import HRISDashboard from "./pages/HRIS/Dashboard";
import HRISExpenseEmployeeAdvance from "./pages/HRIS/Expenses/Advances/EmployeeAdvance";
import { isSettingsModule } from "./config/settingsModules";

// Wrapper: only admins see children; others redirect to dashboard
const AdminOnly = ({ children }) => {
  const { hasRole, loading } = useAuth();
  if (loading) return <Loading />;
  if (!hasRole("ROLE_ADMIN")) return <Navigate to="/dashboard" replace />;
  return children;
};

const hasModuleViewAccess = (can, moduleKey) => {
  const keys = Array.isArray(moduleKey) ? moduleKey : [moduleKey];
  return keys.some(
    (key) =>
      can("view", key) ||
      can("view_own", key) ||
      can("view_department", key) ||
      can("view_global", key)
  );
};

const ModuleRoute = ({ children, moduleKey, auditTaskModuleKey = null }) => {
  const { hasRole, can, loading, capabilitiesLoading } = useAuth();
  const location = useLocation();
  const [assignedAuditAccess, setAssignedAuditAccess] = useState(false);
  const [assignedAuditAccessLoading, setAssignedAuditAccessLoading] = useState(false);
  const isAssignedAuditWorkspace = ["audit", "audit-dashboard"].includes(moduleKey)
    && (location.pathname.startsWith("/audit-process/") || location.pathname === "/audit-dashboard");

  useEffect(() => {
    let active = true;
    if (!isAssignedAuditWorkspace || loading || capabilitiesLoading) return undefined;
    setAssignedAuditAccessLoading(true);
    api.get("/audit/tasks")
      .then(({ data }) => {
        const tasks = Array.isArray(data) ? data : (Array.isArray(data?.content) ? data.content : []);
        const hasActiveAssignment = tasks.some((task) => !["DONE", "CANCELLED"].includes(String(task?.status || "").toUpperCase()));
        if (active) setAssignedAuditAccess(hasActiveAssignment);
      })
      .catch(() => { if (active) setAssignedAuditAccess(false); })
      .finally(() => { if (active) setAssignedAuditAccessLoading(false); });
    return () => { active = false; };
  }, [isAssignedAuditWorkspace, loading, capabilitiesLoading]);

  if (loading || capabilitiesLoading || assignedAuditAccessLoading) return <Loading />;
  // ROLE_DIRECTOR bypasses ordinary modules, but not Settings: those configure the
  // system itself, and a bypass on set-permission would let a Director grant
  // themselves everything else. ROLE_ADMIN still bypasses all, so nobody can be
  // locked out of the screen used to repair permissions.
  if (hasRole("ROLE_ADMIN")) return children;
  if (hasRole("ROLE_DIRECTOR") && !isSettingsModule(moduleKey)) return children;
  const isAuditTaskTab =
    auditTaskModuleKey &&
    location.pathname === "/task" &&
    new URLSearchParams(location.search).get("tab") === "audit-task";
  // Sales may monitor their own project portfolio from the dashboard. The dashboard itself
  // requests an ownership-scoped read model; this does not grant Sales the Audit Process module.
  const isSalesAuditDashboard = moduleKey === "audit-dashboard" && hasRole("ROLE_SALES");
  const hasAccess =
    hasModuleViewAccess(can, moduleKey) ||
    (isAuditTaskTab && hasModuleViewAccess(can, auditTaskModuleKey)) ||
    isSalesAuditDashboard ||
    (isAssignedAuditWorkspace && assignedAuditAccess);
  if (!hasAccess) return <Navigate to="/dashboard" replace />;
  return children;
};

// PmModuleRoute: same as ModuleRoute, but ALSO grants access if the user
// has been added to at least one PM project's team (via the Go backend's
// pm_crm_project_members), independent of role-based Set Permission —
// see usePmProjectMembership.js / NavLinks.jsx for the same OR-condition
// applied to menu visibility. Deliberately a separate component rather
// than modifying ModuleRoute itself, so every other module's route guard
// is provably unaffected by this change.
const PmModuleRoute = ({ children, moduleKey }) => {
  const { hasRole, can, loading, capabilitiesLoading } = useAuth();
  const { isPmProjectMember, loading: pmMembershipLoading } = usePmProjectMembership();
  if (loading || capabilitiesLoading || pmMembershipLoading) return <Loading />;
  if (hasRole("ROLE_ADMIN") || hasRole("ROLE_DIRECTOR")) return children;
  const hasAccess = hasModuleViewAccess(can, moduleKey) || isPmProjectMember;
  if (!hasAccess) return <Navigate to="/dashboard" replace />;
  return children;
};

const FinanceRoute = ({ children }) => {
  const { hasRole, loading } = useAuth();
  if (loading) return <Loading />;
  if (
    !hasRole("ROLE_ADMIN") &&
    !hasRole("ROLE_DIRECTOR") &&
    !hasRole("ROLE_FINANCE")
  )
    return <Navigate to="/dashboard" replace />;
  return children;
};

const OperationManagerRoute = ({ children }) => {
  const { hasRole, loading } = useAuth();
  if (loading) return <Loading />;
  if (!hasRole("ROLE_ADMIN") && !hasRole("ROLE_OPERATION_MANAGER"))
    return <Navigate to="/dashboard" replace />;
  return children;
};

const AuditorUtilizationRoute = ({ children }) => {
  const { hasRole, user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!hasRole("ROLE_OPERATION_MANAGER") && resolveDashboardTier(user) !== "management") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

//clean all console on production environment
const env_app = process.env.REACT_APP_NODE_ENV || "production";
// if (env_app === 'production' || env_app === 'staging'){
//   console.log = () => {};
//   console.debug = () => {};
//   console.info = () => {};
//   console.warn = () => {};
//   console.error = () => {};
// }
const App = () => {
  const { loading, isAuthenticated } = useAuth();

  // Authentication-related pages don't need the layout
  const AuthLayout = ({ children }) => <div className="page">{children}</div>;

  // Show loading while checking authentication
  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <VersionUpdateNotifier />
      <Routes>
        {/* Login route */}
      <Route path="/login" element={
        isAuthenticated() ? <Navigate to="/dashboard" replace /> : <AuthLayout><Login /></AuthLayout>
      } />
      
      {/* Forgot Password route */}
      <Route path="/forgot-password" element={
        isAuthenticated() ? <Navigate to="/dashboard" replace /> : <AuthLayout><ForgotPassword /></AuthLayout>
      } />
      
      {/* Reset Password route */}
      <Route path="/reset-password" element={
        isAuthenticated() ? <Navigate to="/dashboard" replace /> : <AuthLayout><ResetPassword /></AuthLayout>
      } />
      
      {/* Auth-only (no module restriction) */}
      <Route path="/" element={<ProtectedRoute><CombinedDashboard /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><CombinedDashboard /></ProtectedRoute>} />
      <Route path="/crm-dashboard" element={<ProtectedRoute><ModuleRoute moduleKey="crm-dashboard"><CRMDashboard /></ModuleRoute></ProtectedRoute>} />
      <Route path="/audit-dashboard" element={<ProtectedRoute><ModuleRoute moduleKey="audit-dashboard"><AuditDashboard /></ModuleRoute></ProtectedRoute>} />
      <Route path="/audit-reconciliation" element={<ProtectedRoute><ModuleRoute moduleKey="audit-dashboard"><AuditReconciliation /></ModuleRoute></ProtectedRoute>} />
      <Route path="/audit/auditq-transaction-import" element={<ProtectedRoute><ModuleRoute moduleKey="audit-dashboard"><AuditQTransactionImportReview /></ModuleRoute></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      {/* Dev-only select migration harness — never routed in a production build.
          Deliberately NOT wrapped in ProtectedRoute: it renders no real data, and leaving
          it unauthenticated is what lets e2e/tests/select-migration-smoke.spec.ts verify
          menu z-index and clipping against a local dev server without credentials.
          Remove with src/pages/DevSelects once the migration is signed off. */}
      {process.env.NODE_ENV !== 'production' && (
        <Route path="/dev/selects" element={<DevSelects />} />
      )}
      <Route path="/sales-target" element={<ProtectedRoute><ModuleRoute moduleKey="sales-target"><SalesTarget /></ModuleRoute></ProtectedRoute>} />

       
      {/* CRM module routes */}
      <Route path="/leads" element={<ProtectedRoute><ModuleRoute moduleKey="leads"><Leads /></ModuleRoute></ProtectedRoute>} />
      <Route path="/leads/detail/:id" element={<ProtectedRoute><ModuleRoute moduleKey="leads"><LeadDetail /></ModuleRoute></ProtectedRoute>} />
      <Route path="/proposals" element={<ProtectedRoute><ModuleRoute moduleKey="proposal"><Proposals /></ModuleRoute></ProtectedRoute>} />
      <Route path="/proposals/detail/:id" element={<ProtectedRoute><ModuleRoute moduleKey="proposal"><DetailProposal /></ModuleRoute></ProtectedRoute>} />
      <Route path="/proposals/edit/:id" element={<ProtectedRoute><ModuleRoute moduleKey="proposal"><EditProposal /></ModuleRoute></ProtectedRoute>} />
      <Route path="/proposals/view/:id" element={<ProtectedRoute><ModuleRoute moduleKey="proposal"><EditProposal /></ModuleRoute></ProtectedRoute>} />
      <Route path="/proposals/:id/preview" element={<ProtectedRoute><ModuleRoute moduleKey="proposal"><ProposalPreview /></ModuleRoute></ProtectedRoute>} />
      <Route path="/company" element={<ProtectedRoute><ModuleRoute moduleKey="prospect"><Company /></ModuleRoute></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><ModuleRoute moduleKey="client"><ClientsList /></ModuleRoute></ProtectedRoute>} />
      <Route path="/clients/:id" element={<ProtectedRoute><ModuleRoute moduleKey="client"><Clients /></ModuleRoute></ProtectedRoute>} />
      <Route path="/audit/clients" element={<ProtectedRoute><ModuleRoute moduleKey="audit"><ClientsList auditMode /></ModuleRoute></ProtectedRoute>} />
      <Route path="/audit/clients/:id" element={<ProtectedRoute><ModuleRoute moduleKey="audit"><AuditClientDetail /></ModuleRoute></ProtectedRoute>} />
      <Route path="/audit/client-matrix" element={<ProtectedRoute><ModuleRoute moduleKey="audit"><ClientProjectMatrix /></ModuleRoute></ProtectedRoute>} />
      <Route path="/audit/sales-client-dashboard" element={<ProtectedRoute><ModuleRoute moduleKey="audit"><SalesClientDashboard /></ModuleRoute></ProtectedRoute>} />
      <Route path="/invoice" element={<ProtectedRoute><ModuleRoute moduleKey="invoice"><Invoice /></ModuleRoute></ProtectedRoute>} />
      <Route path="/invoice/:id" element={<ProtectedRoute><ModuleRoute moduleKey="invoice"><InvoiceView /></ModuleRoute></ProtectedRoute>} />
      <Route path="/payment" element={<ProtectedRoute><ModuleRoute moduleKey="payment"><Payment /></ModuleRoute></ProtectedRoute>} />
      <Route path="/project" element={<ProtectedRoute><ModuleRoute moduleKey="project"><Project /></ModuleRoute></ProtectedRoute>} />
      <Route path="/project-monitoring" element={<ProtectedRoute><ModuleRoute moduleKey="project"><SalesProjectMonitoring /></ModuleRoute></ProtectedRoute>} />
      <Route path="/projects/detail/:id" element={<ProtectedRoute><ModuleRoute moduleKey="project"><DetailProject /></ModuleRoute></ProtectedRoute>} />
      <Route path="/projects/edit/:id" element={<ProtectedRoute><ModuleRoute moduleKey="project"><EditProject /></ModuleRoute></ProtectedRoute>} />
      <Route path="/task" element={<ProtectedRoute><ModuleRoute moduleKey="task" auditTaskModuleKey="audit"><Task /></ModuleRoute></ProtectedRoute>} />
      <Route path="/expense" element={<ProtectedRoute><ModuleRoute moduleKey="expense"><Expense /></ModuleRoute></ProtectedRoute>} />
      <Route path="/expense/:id" element={<ProtectedRoute><ModuleRoute moduleKey="expense"><ExpenseDetail /></ModuleRoute></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><ModuleRoute moduleKey="calendar"><Calendar /></ModuleRoute></ProtectedRoute>} />
      <Route path="/project-management/*" element={<ProtectedRoute><ModuleRoute moduleKey="pm-library"><ProjectManagement /></ModuleRoute></ProtectedRoute>} />
      <Route path="/pm/dashboard" element={<ProtectedRoute><ModuleRoute moduleKey="pm-dashboard"><PmDashboard /></ModuleRoute></ProtectedRoute>} />
      <Route path="/pm/calendar" element={<ProtectedRoute><ModuleRoute moduleKey="pm-dashboard"><Navigate to="/pm/dashboard" replace /></ModuleRoute></ProtectedRoute>} />
      <Route path="/pm/projects" element={<ProtectedRoute><PmModuleRoute moduleKey="pm-projects"><PmProjects /></PmModuleRoute></ProtectedRoute>} />
      <Route path="/pm/projects/:id" element={<ProtectedRoute><PmModuleRoute moduleKey="pm-projects"><PmProjectDetail /></PmModuleRoute></ProtectedRoute>} />
      <Route path="/pm/timesheet" element={<ProtectedRoute><ModuleRoute moduleKey="pm-timesheet"><PmTimesheet /></ModuleRoute></ProtectedRoute>} />

        {/* HRIS */}
        {/* Open to every logged-in user (no module gate); competency roster is read-only. */}
        <Route path="/hris/dashboard" element={<ProtectedRoute><HRISDashboard /></ProtectedRoute>} />
        <Route
          path="/hris/expenses/dashboard"
          element={
            <ProtectedRoute>
              <ModuleRoute moduleKey="hris">
                <HRISExpenseDashboard />
              </ModuleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hris/employees"
          element={
            <ProtectedRoute>
              <ModuleRoute moduleKey={["hris", "employee"]}>
                <HRISEmployees />
              </ModuleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hris/employees/:id"
          element={
            <ProtectedRoute>
              <ModuleRoute moduleKey="hris">
                <HRISEmployeeDetail />
              </ModuleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hris/expenses/claims/expense-claim"
          element={
            <ProtectedRoute>
              <ModuleRoute moduleKey="hris">
                <HRISExpenseClaim />
              </ModuleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hris/department"
          element={
            <ProtectedRoute>
              <ModuleRoute moduleKey={["hris", "department"]}>
                <Department />
              </ModuleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hris/job-title"
          element={
            <ProtectedRoute>
              <ModuleRoute moduleKey="hris">
                <JobTitle />
              </ModuleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hris/branch"
          element={
            <ProtectedRoute>
              <ModuleRoute moduleKey="hris">
                <Branch />
              </ModuleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hris/grades"
          element={<ProtectedRoute><ModuleRoute moduleKey="hris"><DummyEmployeeManagementPage pageKey="grades" /></ModuleRoute></ProtectedRoute>}
        />
        <Route
          path="/hris/organization-structure"
          element={<ProtectedRoute><ModuleRoute moduleKey="hris"><DummyEmployeeManagementPage pageKey="organization-structure" /></ModuleRoute></ProtectedRoute>}
        />
        <Route
          path="/hris/competencies"
          element={<ProtectedRoute><ModuleRoute moduleKey="hris"><DummyEmployeeManagementPage pageKey="competencies" /></ModuleRoute></ProtectedRoute>}
        />
        <Route
          path="/hris/employee-assignments"
          element={<ProtectedRoute><ModuleRoute moduleKey="hris"><DummyEmployeeManagementPage pageKey="employee-assignments" /></ModuleRoute></ProtectedRoute>}
        />
        <Route
          path="/hris/employment-administration"
          element={<ProtectedRoute><ModuleRoute moduleKey="hris"><DummyEmployeeManagementPage pageKey="employment-administration" /></ModuleRoute></ProtectedRoute>}
        />
        <Route
          path="/hris/employee-documents"
          element={<ProtectedRoute><ModuleRoute moduleKey="hris"><DummyEmployeeManagementPage pageKey="employee-documents" /></ModuleRoute></ProtectedRoute>}
        />
        <Route
          path="/hris/employee-reports"
          element={<ProtectedRoute><ModuleRoute moduleKey="hris"><DummyEmployeeManagementPage pageKey="employee-reports" /></ModuleRoute></ProtectedRoute>}
        />
        <Route
          path="/hris/leaves"
          element={
            <ProtectedRoute>
              <ModuleRoute moduleKey="hris">
                <HRISLeaves />
              </ModuleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hris/payroll"
          element={
            <ProtectedRoute>
              <ModuleRoute moduleKey="hris">
                <HRISPayroll />
              </ModuleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hris/performance"
          element={
            <ProtectedRoute>
              <ModuleRoute moduleKey="hris">
                <HRISPerformance />
              </ModuleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hris/recruitment"
          element={<ProtectedRoute><ModuleRoute moduleKey="hris"><DummyRecruitmentManagementPage pageKey="dashboard" /></ModuleRoute></ProtectedRoute>}
        />
        <Route
          path="/hris/recruitment/manpower-request"
          element={<ProtectedRoute><ModuleRoute moduleKey="hris"><DummyRecruitmentManagementPage pageKey="manpower-request" /></ModuleRoute></ProtectedRoute>}
        />
        <Route
          path="/hris/recruitment/job-requisition"
          element={<ProtectedRoute><ModuleRoute moduleKey="hris"><DummyRecruitmentManagementPage pageKey="job-requisition" /></ModuleRoute></ProtectedRoute>}
        />
        <Route
          path="/hris/recruitment/job-vacancies"
          element={<ProtectedRoute><ModuleRoute moduleKey="hris"><DummyRecruitmentManagementPage pageKey="job-vacancies" /></ModuleRoute></ProtectedRoute>}
        />
        <Route
          path="/hris/recruitment/candidates"
          element={<ProtectedRoute><ModuleRoute moduleKey="hris"><DummyRecruitmentManagementPage pageKey="candidates" /></ModuleRoute></ProtectedRoute>}
        />
        <Route
          path="/hris/recruitment/selection-process"
          element={<ProtectedRoute><ModuleRoute moduleKey="hris"><DummyRecruitmentManagementPage pageKey="selection-process" /></ModuleRoute></ProtectedRoute>}
        />
        <Route
          path="/hris/recruitment/job-offering"
          element={<ProtectedRoute><ModuleRoute moduleKey="hris"><DummyRecruitmentManagementPage pageKey="job-offering" /></ModuleRoute></ProtectedRoute>}
        />
        <Route
          path="/hris/recruitment/hiring-approval"
          element={<ProtectedRoute><ModuleRoute moduleKey="hris"><DummyRecruitmentManagementPage pageKey="hiring-approval" /></ModuleRoute></ProtectedRoute>}
        />
        <Route
          path="/hris/recruitment/pre-onboarding"
          element={<ProtectedRoute><ModuleRoute moduleKey="hris"><DummyRecruitmentManagementPage pageKey="pre-onboarding" /></ModuleRoute></ProtectedRoute>}
        />
        <Route
          path="/hris/recruitment/reports"
          element={<ProtectedRoute><ModuleRoute moduleKey="hris"><DummyRecruitmentManagementPage pageKey="recruitment-reports" /></ModuleRoute></ProtectedRoute>}
        />
        <Route
          path="/hris/recruitment-legacy"
          element={
            <ProtectedRoute>
              <ModuleRoute moduleKey="hris">
                <HRISRecruitment />
              </ModuleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hris/shift-attendance"
          element={
            <ProtectedRoute>
              <ModuleRoute moduleKey="hris">
                <HRISShiftAttendance />
              </ModuleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hris/tax-benefits"
          element={
            <ProtectedRoute>
              <ModuleRoute moduleKey="hris">
                <HRISTaxBenefits />
              </ModuleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hris/tenure"
          element={
            <ProtectedRoute>
              <ModuleRoute moduleKey="hris">
                <HRISTenure />
              </ModuleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hris/expenses/claims/expense-claim-type"
          element={
            <ProtectedRoute>
              <ModuleRoute moduleKey="hris">
                <HRISExpenseClaimTypes />
              </ModuleRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hris/expenses/advances/employee-advance"
          element={
            <ProtectedRoute>
              <ModuleRoute moduleKey="hris">
                <HRISExpenseEmployeeAdvance />
              </ModuleRoute>
            </ProtectedRoute>
          }
        />

      {/* Reports */}
      <Route path="/reports/company" element={<ProtectedRoute><ModuleRoute moduleKey="prospect"><ProspectsReports /></ModuleRoute></ProtectedRoute>} />
      <Route path="/reports/leads" element={<ProtectedRoute><ModuleRoute moduleKey="leads"><LeadsReports /></ModuleRoute></ProtectedRoute>} />
      <Route path="/reports/proposals" element={<ProtectedRoute><ModuleRoute moduleKey="proposal"><ProposalsReports /></ModuleRoute></ProtectedRoute>} />
      <Route path="/reports/projects" element={<ProtectedRoute><ModuleRoute moduleKey="project"><ProjectsReports /></ModuleRoute></ProtectedRoute>} />
      <Route path="/reports/sales-report" element={<ProtectedRoute><ModuleRoute moduleKey="sales-report"><SalesReport /></ModuleRoute></ProtectedRoute>} />
      <Route path="/reports/achievement-form" element={<ProtectedRoute><ModuleRoute moduleKey="achievement"><AchievementForm /></ModuleRoute></ProtectedRoute>} />
      <Route path="/analysis" element={<ProtectedRoute><ModuleRoute moduleKey="analysis"><Analysis /></ModuleRoute></ProtectedRoute>} />

      {/* Audit Platform */}
      <Route path="/audit-libraries/:version" element={<ProtectedRoute><ModuleRoute moduleKey="audit"><AuditLibraries /></ModuleRoute></ProtectedRoute>} />
      <Route path="/audit/form-entry" element={<ProtectedRoute><ModuleRoute moduleKey="audit"><AuditFormEntry /></ModuleRoute></ProtectedRoute>} />
      <Route path="/audit/form-catalog" element={<ProtectedRoute><ModuleRoute moduleKey="audit"><AuditFormCatalog /></ModuleRoute></ProtectedRoute>} />
      <Route path="/audit-output-template" element={<ProtectedRoute><ModuleRoute moduleKey="audit-output-template"><AuditOutputTemplate /></ModuleRoute></ProtectedRoute>} />
      <Route path="/audit-schedule" element={<ProtectedRoute><ModuleRoute moduleKey="audit"><AuditSchedule /></ModuleRoute></ProtectedRoute>} />
      <Route path="/audit-process/:projectId?" element={<ProtectedRoute><ModuleRoute moduleKey="audit"><AuditProcess /></ModuleRoute></ProtectedRoute>} />
      <Route path="/audit-findings/:projectId" element={<ProtectedRoute><ModuleRoute moduleKey="audit"><AuditFindings /></ModuleRoute></ProtectedRoute>} />
      <Route path="/audit/certificate-monitoring" element={<ProtectedRoute><ModuleRoute moduleKey="audit"><CertificateMonitoring /></ModuleRoute></ProtectedRoute>} />
      <Route path="/audit/competency-management" element={<ProtectedRoute><ModuleRoute moduleKey="audit"><CompetencyManagement /></ModuleRoute></ProtectedRoute>} />
      <Route path="/audit/finance-confirmation" element={<ProtectedRoute><FinanceRoute><FinanceConfirmation /></FinanceRoute></ProtectedRoute>} />
      <Route path="/tools/load-balancer" element={<ProtectedRoute><OperationManagerRoute><LoadBalancer /></OperationManagerRoute></ProtectedRoute>} />
      <Route path="/tools/auditor-utilization" element={<ProtectedRoute><AuditorUtilizationRoute><AuditorUtilization /></AuditorUtilizationRoute></ProtectedRoute>} />
       {/* Settings: gated per module via Set Permission */}
      <Route path="/create-template" element={<ProtectedRoute><ModuleRoute moduleKey="create-template"><CreateTemplate /></ModuleRoute></ProtectedRoute>} />
      <Route path="/approval-workflow" element={<ProtectedRoute><ModuleRoute moduleKey="workflow"><ApprovalWorkflow /></ModuleRoute></ProtectedRoute>} />
      <Route path="/rate-management" element={<ProtectedRoute><ModuleRoute moduleKey="rate-management"><RateManagement /></ModuleRoute></ProtectedRoute>} />
      <Route path="/create-type-unit" element={<ProtectedRoute><ModuleRoute moduleKey="create-type-unit"><CreateTypeUnit /></ModuleRoute></ProtectedRoute>} />
      <Route path="/proposal-stage" element={<ProtectedRoute><ModuleRoute moduleKey="proposal-stage"><ProposalStageManagement /></ModuleRoute></ProtectedRoute>} />
      <Route path="/master-currency" element={<ProtectedRoute><ModuleRoute moduleKey="master-currency"><MasterCurrency /></ModuleRoute></ProtectedRoute>} />
      <Route path="/master-currency/:id" element={<ProtectedRoute><ModuleRoute moduleKey="master-currency"><CurrencyDetail /></ModuleRoute></ProtectedRoute>} />
      <Route path="/format-number" element={<ProtectedRoute><ModuleRoute moduleKey="format-number"><FormatNumber /></ModuleRoute></ProtectedRoute>} />
      <Route path="/expense-category" element={<ProtectedRoute><ModuleRoute moduleKey="expense-category"><ExpenseCategory /></ModuleRoute></ProtectedRoute>} />
      <Route path="/chart-of-account" element={<ProtectedRoute><ModuleRoute moduleKey="chart-of-account"><ChartOfAccount /></ModuleRoute></ProtectedRoute>} />
      <Route path="/create-role" element={<ProtectedRoute><ModuleRoute moduleKey="create-role"><CreateRole /></ModuleRoute></ProtectedRoute>} />
      <Route path="/set-permission" element={<ProtectedRoute><ModuleRoute moduleKey="set-permission"><SetPermission /></ModuleRoute></ProtectedRoute>} />
      <Route path="/email-template" element={<ProtectedRoute><ModuleRoute moduleKey="email-template"><EmailTemplate /></ModuleRoute></ProtectedRoute>} />
      <Route path="/email-configuration" element={<ProtectedRoute><ModuleRoute moduleKey="email-configuration"><EmailConfiguration /></ModuleRoute></ProtectedRoute>} />
      {/* Default redirect */}
      <Route path="*" element={
        isAuthenticated() ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
      } />
      </Routes>
    </>
  );
};

export default App;
